import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Empty from 'src/common/components/Empty';
import { LanguageKeys } from 'src/common/constants/languages';
import { frostedSegmentTabsSx } from 'src/common/styles/frostedPanelStyles';
import { distanceKmFromId, normalizeStatistic, parseDistanceKm, sortDistanceIds } from 'src/helpers/statisticExtras';
import { TStatistics } from 'src/interfaces/Statistics';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';
import DistanceInsightsPanel from './DistanceInsightsPanel';
import InsightsPanelSurface from './InsightsPanelSurface';

interface Props {
  competitionId: string;
  distances: string[];
  statistics?: TStatistics;
  currentLang: LanguageKeys;
}

export default function DistanceInsightsTabs({
  competitionId,
  distances,
  statistics,
  currentLang,
}: Props) {
  const { t } = useTranslation();
  const { distancesById } = useSystemOptionsStore(s => s);

  const sortedDistances = useMemo(
    () => sortDistanceIds(distances, distancesById),
    [distances, distancesById],
  );

  const [activeDistanceId, setActiveDistanceId] = useState<string | null>(null);

  useEffect(() => {
    if (!sortedDistances.length) {
      setActiveDistanceId(null);
      return;
    }
    setActiveDistanceId(prev =>
      prev && sortedDistances.includes(prev) ? prev : sortedDistances[0] ?? null,
    );
  }, [sortedDistances]);

  const activeDistance = activeDistanceId ? distancesById?.[activeDistanceId] : undefined;
  const activeDistanceKm = useMemo(() => {
    if (!activeDistance) return undefined;
    return (
      distanceKmFromId(activeDistanceId ?? undefined, distancesById) ??
      parseDistanceKm(activeDistance.name) ??
      parseDistanceKm(activeDistance.i18n[currentLang] ?? '')
    );
  }, [activeDistance, activeDistanceId, currentLang, distancesById]);

  const activeStatistic = useMemo(() => {
    if (!activeDistanceId || !statistics) return undefined;
    const raw = statistics[`${competitionId}:${activeDistanceId}`];
    return raw ? normalizeStatistic(raw, activeDistanceKm) : undefined;
  }, [activeDistanceId, activeDistanceKm, competitionId, statistics]);

  const selectedDistanceTab = activeDistanceId ?? sortedDistances[0] ?? false;

  if (!sortedDistances.length) {
    return null;
  }

  return (
    <>
      <Tabs
        value={selectedDistanceTab}
        onChange={(_, value) => setActiveDistanceId(value)}
        sx={theme => ({ ...frostedSegmentTabsSx(theme) })}
        variant="scrollable"
        scrollButtons="auto"
      >
        {sortedDistances.map(distId => {
          const distance = distancesById?.[distId];
          if (!distance) return null;
          return <Tab key={distId} value={distId} label={distance.i18n[currentLang]} />;
        })}
      </Tabs>
      {activeDistance && activeStatistic ? (
        <DistanceInsightsPanel
          title={activeDistance.i18n[currentLang]}
          statistic={activeStatistic}
          distanceKm={activeDistanceKm}
        />
      ) : (
        <InsightsPanelSurface>
          <Empty
            embedded
            icon={<BarChartOutlinedIcon fontSize="large" />}
            title={t('statistics.notEnoughData')}
            description={t('insights.noSavedData')}
          />
        </InsightsPanelSurface>
      )}
    </>
  );
}
